import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getTodayLocal, formatDateToLocal } from '../lib/dateUtils';
import {
  Settings, CheckCircle2, XCircle, RefreshCw, ClipboardList, Clock,
  ShieldCheck, AlertCircle, Eye, EyeOff, Trash2, ShieldAlert, Calendar,
  Users2, AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EtimeofficeSettingsPage
 * ------------------------
 * Settings → Attendance Machine. Connect, test, sync, review.
 * Every mutation is admin-triggered. Nothing applies until the approval
 * queue is processed.
 */
const EtimeofficeSettingsPage = () => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ corporate_id: '', username: '', password: '', enabled: true });
  const [showPw, setShowPw] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncFrom, setSyncFrom] = useState(_lastNDays(7));
  const [syncTo, setSyncTo] = useState(_today());
  const [lastTest, setLastTest] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [syncLog, setSyncLog] = useState([]);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('adminToken')}` });

  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/etimeoffice/config`, { headers: auth() });
      setCfg(r.data);
      if (r.data.configured) {
        setForm({
          corporate_id: r.data.corporate_id || '',
          username: r.data.username || '',
          password: '',  // never repopulate
          enabled: r.data.enabled,
        });
      }
    } catch (e) {
      toast.error('Failed to load config');
    }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/etimeoffice/sync-log?limit=10`, { headers: auth() });
      setSyncLog(r.data.items || []);
    } catch (e) { /* best-effort */ }
  }, []);

  useEffect(() => { loadConfig(); loadLog(); }, [loadConfig, loadLog]);

  const saveConfig = async () => {
    if (!form.corporate_id || !form.username || !form.password) {
      toast.error('Corporate ID, Username and Password are all required');
      return;
    }
    try {
      await axios.put(`${API}/api/admin/etimeoffice/config`, form, { headers: auth() });
      toast.success('Credentials saved');
      setForm((p) => ({ ...p, password: '' }));
      loadConfig();
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setLastTest(null);
    try {
      const r = await axios.post(`${API}/api/admin/etimeoffice/test-connection`, {}, {
        headers: auth(),
        timeout: 60000,
      });
      setLastTest(r.data);
      if (r.data.ok) toast.success(`Connection OK — ${r.data.message}`);
      else toast.error(`Connection failed: ${r.data.message}`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setLastTest({ ok: false, message: msg });
      toast.error(`Connection failed: ${msg}`);
    } finally {
      setTesting(false);
      loadConfig();
    }
  };

  const runSync = async () => {
    setSyncing(true);
    setLastSync(null);
    try {
      // Step 1 — fire-and-forget; returns 202 with log_id in <1s.
      const r = await axios.post(`${API}/api/admin/etimeoffice/sync`,
        { from_date: syncFrom, to_date: syncTo },
        { headers: auth(), timeout: 15000 });
      const logId = r.data.log_id;
      toast.info(`Sync queued · polling for progress…`);

      // Step 2 — poll every 3s until done. Cap at 10 minutes.
      const startedAt = Date.now();
      const TIMEOUT_MS = 10 * 60 * 1000;
      let lastStatus = '';
      while (Date.now() - startedAt < TIMEOUT_MS) {
        await new Promise((res) => setTimeout(res, 3000));
        let status;
        try {
          const p = await axios.get(`${API}/api/admin/etimeoffice/sync-log/${logId}`,
            { headers: auth(), timeout: 10000 });
          status = p.data;
        } catch (pollErr) {
          // transient poll failure — keep trying
          continue;
        }
        if (status.status !== lastStatus) {
          lastStatus = status.status;
          if (status.status === 'fetching') toast.info('Fetching machine data…', { id: 'sync-progress' });
          else if (status.status === 'staging') toast.info('Staging records for approval…', { id: 'sync-progress' });
        }
        if (status.status === 'ok') {
          setLastSync({
            ok: true,
            rows_fetched: status.rows_fetched,
            mappings_staged: status.mappings_staged || 0,
            absences_staged: status.absences_staged || 0,
            compoffs_staged: status.compoffs_staged || 0,
            conflicts_staged: status.conflicts_staged || 0,
          });
          const total = (status.mappings_staged || 0) + (status.absences_staged || 0)
            + (status.compoffs_staged || 0) + (status.conflicts_staged || 0);
          if (total > 0) toast.success(`Synced ${status.rows_fetched} rows · ${total} actions need your approval`, { id: 'sync-progress' });
          else if (status.rows_fetched > 0) toast.success(`Synced ${status.rows_fetched} rows · nothing new to approve`, { id: 'sync-progress' });
          else toast.info('Sync OK — no punch records in this date range', { id: 'sync-progress' });
          loadConfig();
          loadLog();
          return;
        }
        if (status.status === 'failed') {
          toast.error(`Sync failed: ${status.error || 'unknown error'}`, { id: 'sync-progress' });
          loadLog();
          return;
        }
      }
      toast.error('Sync still running after 10 minutes — check Sync history later.');
      loadLog();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 409) {
        toast.error(`Another sync is already running. Wait for it to finish, then retry.`);
        loadLog();
      } else {
        toast.error(`Sync failed: ${detail}`);
      }
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="etimeoffice-settings-page">
      <header className="mb-6">
        <div className="flex items-center gap-2">
          <Settings className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Attendance Machine — E-TIMEOFFICE</h1>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Connect the biometric machine API, pull punch data on demand, and
          review every change before it touches employee records.
        </p>
      </header>

      <ApprovalGate />

      {/* Configuration card */}
      <section className="bg-white border border-gray-200 rounded-lg p-6 mb-5" data-testid="eto-config-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium text-gray-900">Connection settings</h2>
          {cfg?.configured && (
            <span className="text-xs text-gray-500">
              Last updated {_fmtDate(cfg.updated_at)}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Corporate ID">
            <input
              type="text"
              value={form.corporate_id}
              onChange={(e) => setForm((p) => ({ ...p, corporate_id: e.target.value }))}
              data-testid="eto-corporate-id"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="HALFEATENDONUT"
            />
          </Field>
          <Field label="Username">
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
              data-testid="eto-username"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </Field>
          <Field label="Password">
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                data-testid="eto-password"
                className="w-full px-3 py-2 pr-9 border border-gray-300 rounded-md text-sm"
                placeholder={cfg?.password_set ? `${cfg.password_preview} (saved)` : 'enter new password'}
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                aria-label="Toggle password visibility"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>
          <Field label="Integration enabled">
            <label className="flex items-center gap-2 mt-1 cursor-pointer">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
                data-testid="eto-enabled"
                className="rounded"
              />
              <span className="text-sm text-gray-700">
                {form.enabled ? 'Enabled — sync calls will run' : 'Disabled — connection blocked'}
              </span>
            </label>
          </Field>
        </div>
        <div className="flex gap-2 mt-5">
          <button
            onClick={saveConfig}
            data-testid="eto-save-config"
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
          >
            Save credentials
          </button>
          <button
            onClick={testConnection}
            disabled={testing || !cfg?.configured}
            data-testid="eto-test-connection"
            className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 inline-flex items-center gap-2 disabled:opacity-50"
          >
            {testing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
            Test connection
          </button>
        </div>

        {lastTest && (
          <div
            className={`mt-3 p-3 border rounded-md text-sm inline-flex items-center gap-2 ${
              lastTest.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
            }`}
            data-testid="eto-test-result"
          >
            {lastTest.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {lastTest.ok ? `Connected · ${lastTest.message}` : `Failed: ${lastTest.message}`}
            {lastTest.ok && lastTest.is_admin && <span className="text-xs">(admin scope)</span>}
          </div>
        )}
        {cfg?.last_test_at && !lastTest && (
          <p className="text-xs text-gray-500 mt-2">
            Last test {_fmtDate(cfg.last_test_at)} · status: {cfg.last_test_status}
          </p>
        )}
      </section>

      {/* Manual sync */}
      <section className="bg-white border border-gray-200 rounded-lg p-6 mb-5" data-testid="eto-sync-card">
        <h2 className="font-medium text-gray-900 mb-4">Manual sync</h2>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="From">
            <input
              type="date"
              value={syncFrom}
              onChange={(e) => setSyncFrom(e.target.value)}
              data-testid="eto-sync-from"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </Field>
          <Field label="To">
            <input
              type="date"
              value={syncTo}
              onChange={(e) => setSyncTo(e.target.value)}
              data-testid="eto-sync-to"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </Field>
          <button
            onClick={runSync}
            disabled={syncing || !cfg?.configured}
            data-testid="eto-sync-now"
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-50"
          >
            {syncing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Sync window is capped at 92 days per request. Each sync stages
          changes for review — nothing affects employee records until you
          approve in the queue.
        </p>

        {lastSync && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="eto-sync-result">
            <Stat label="Rows fetched" value={lastSync.rows_fetched} />
            <Stat label="Daily rows updated" value={lastSync.roll_ups_upserted} />
            <Stat label="Mappings to approve" value={lastSync.mappings_staged} highlight={lastSync.mappings_staged > 0} />
            <Stat label="Comp-offs to approve" value={lastSync.compoffs_staged} highlight={lastSync.compoffs_staged > 0} />
            <Stat label="Absences flagged" value={lastSync.absences_staged} highlight={lastSync.absences_staged > 0} />
          </div>
        )}

        {cfg?.last_sync_at && (
          <p className="text-xs text-gray-500 mt-2">Last successful sync · {_fmtDate(cfg.last_sync_at)}</p>
        )}
      </section>

      {/* Sync log */}
      <section className="bg-white border border-gray-200 rounded-lg p-6 mb-5" data-testid="eto-sync-log">
        <h2 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Sync history
        </h2>
        {syncLog.length === 0 ? (
          <p className="text-sm text-gray-500">No syncs yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Started</th>
                <th className="text-left px-3 py-2">Range</th>
                <th className="text-right px-3 py-2">Rows</th>
                <th className="text-right px-3 py-2">Staged</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {syncLog.map((s) => {
                const staged = (s.mappings_staged || 0) + (s.absences_staged || 0) + (s.compoffs_staged || 0);
                const canDiscard = s.status === 'ok';
                return (
                  <tr key={s._id} className="border-t border-gray-100" data-testid={`sync-row-${s._id}`}>
                    <td className="px-3 py-2 text-gray-700">{_fmtDate(s.started_at)}</td>
                    <td className="px-3 py-2 text-gray-700">{s.from_date} → {s.to_date}</td>
                    <td className="px-3 py-2 text-right">{s.rows_fetched ?? '—'}</td>
                    <td className="px-3 py-2 text-right">{staged}</td>
                    <td className="px-3 py-2">
                      {s.status === 'ok' ? (
                        <span className="text-green-700 inline-flex items-center gap-1 text-xs"><CheckCircle2 className="w-3 h-3" />ok</span>
                      ) : s.status === 'failed' ? (
                        <span className="text-red-700 inline-flex items-center gap-1 text-xs" title={s.error}><XCircle className="w-3 h-3" />failed</span>
                      ) : s.status === 'discarded' ? (
                        <span className="text-gray-500 inline-flex items-center gap-1 text-xs"><Trash2 className="w-3 h-3" />discarded</span>
                      ) : (
                        <span className="text-amber-700 inline-flex items-center gap-1 text-xs"><RefreshCw className="w-3 h-3 animate-spin" />{s.status}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canDiscard && (
                        <DiscardButton syncId={s._id} onDone={loadLog} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <DataQualitySection cfg={cfg} onChange={loadConfig} />
      <MappingsManagementSection />
    </div>
  );
};

// ─── Approval-gate banner with live counts ──────────────────────────────────

const ApprovalGate = () => {
  const [counts, setCounts] = useState({ empcode_mapping: 0, comp_off_credit: 0, unauthorized_absence: 0, total_pending: 0 });
  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('adminToken')}` });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/etimeoffice/pending-actions?status=pending&limit=1`, { headers: auth() });
        if (!cancelled) setCounts({ ...r.data.pending_counts, total_pending: r.data.total_pending });
      } catch (e) { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (counts.total_pending === 0) {
    return (
      <div className="mb-5 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800 inline-flex items-center gap-2" data-testid="eto-approval-gate-clear">
        <ShieldCheck className="w-4 h-4" /> Approval queue is clear.
      </div>
    );
  }
  return (
    <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-md flex items-center justify-between" data-testid="eto-approval-gate">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <p className="font-medium mb-1">{counts.total_pending} action(s) awaiting your approval</p>
          <p className="text-xs">
            {counts.empcode_mapping} mapping(s) ·
            {' '}{counts.comp_off_credit} comp-off credit(s) ·
            {' '}{counts.unauthorized_absence} unauthorised absence(s)
          </p>
        </div>
      </div>
      <a
        href="/admin/dashboard?page=etimeoffice-queue"
        data-testid="eto-go-to-queue"
        className="px-3 py-2 bg-amber-600 text-white rounded-md text-sm hover:bg-amber-700 inline-flex items-center gap-2"
      >
        <ClipboardList className="w-3.5 h-3.5" /> Review queue
      </a>
    </div>
  );
};

// ─── Tiny utilities ────────────────────────────────────────────────────────

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

const Stat = ({ label, value, highlight }) => (
  <div className={`border rounded-md p-2 ${highlight ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
    <div className="text-xs text-gray-600">{label}</div>
    <div className={`text-lg font-semibold ${highlight ? 'text-amber-800' : 'text-gray-900'}`}>{value ?? 0}</div>
  </div>
);

const _today = () => getTodayLocal();
const _lastNDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return formatDateToLocal(d);
};
const _fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

// ─── Discard sync button (with confirmation) ───────────────────────────────

const DiscardButton = ({ syncId, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('adminToken')}` });

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/etimeoffice/sync-log/${syncId}/discard`, {}, { headers: auth() });
      toast.success(`Discarded · removed ${r.data.daily_rows_deleted} rows + ${r.data.pending_actions_deleted} pending actions`);
      onDone?.();
    } catch (e) {
      toast.error(`Discard failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (!confirming) {
    return (
      <button
        onClick={() => setConfirming(true)}
        data-testid={`sync-discard-${syncId}`}
        className="text-xs text-red-600 hover:text-red-800 hover:underline inline-flex items-center gap-1"
      >
        <Trash2 className="w-3 h-3" /> Discard
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1.5 text-xs">
      <span className="text-red-700">Confirm?</span>
      <button
        onClick={run}
        disabled={busy}
        data-testid={`sync-discard-confirm-${syncId}`}
        className="px-2 py-0.5 bg-red-600 text-white rounded text-[11px] hover:bg-red-700 disabled:opacity-50"
      >
        {busy ? 'Working…' : 'Yes, discard'}
      </button>
      <button
        onClick={() => setConfirming(false)}
        className="px-2 py-0.5 border border-gray-200 text-gray-600 rounded text-[11px]"
      >
        Cancel
      </button>
    </div>
  );
};

// ─── Data Quality section (cutoff date + date-range purge + diagnostics) ───

const DataQualitySection = ({ cfg, onChange }) => {
  const [cutoff, setCutoff] = useState('');
  const [saving, setSaving] = useState(false);
  const [purgeFrom, setPurgeFrom] = useState('');
  const [purgeTo, setPurgeTo] = useState('');
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purging, setPurging] = useState(false);
  const [summary, setSummary] = useState(null);
  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('adminToken')}` });

  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/etimeoffice/data-quality/summary`, { headers: auth() });
      setSummary(r.data);
    } catch (e) { /* best-effort */ }
  }, []);

  useEffect(() => {
    setCutoff(cfg?.reports_data_cutoff_date || '');
  }, [cfg?.reports_data_cutoff_date]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const saveCutoff = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/etimeoffice/config/cutoff`,
        { cutoff_date: cutoff || null },
        { headers: auth() });
      toast.success(cutoff ? `Reports will ignore machine data before ${cutoff}` : 'Cutoff cleared');
      onChange?.();
      loadSummary();
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const runPurge = async () => {
    if (purgeConfirm !== 'PURGE') {
      toast.error('Type PURGE exactly to confirm.');
      return;
    }
    if (!purgeFrom || !purgeTo) {
      toast.error('Both dates required.');
      return;
    }
    setPurging(true);
    try {
      const r = await axios.post(`${API}/api/admin/etimeoffice/attendance/purge`, {
        from_date: purgeFrom, to_date: purgeTo, confirm: 'PURGE',
      }, { headers: auth() });
      toast.success(`Purged · ${r.data.daily_rows_deleted} rows + ${r.data.pending_actions_deleted} pending actions`);
      setPurgeFrom(''); setPurgeTo(''); setPurgeConfirm('');
      loadSummary();
    } catch (e) {
      toast.error(`Purge failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setPurging(false);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-6 mb-5" data-testid="eto-data-quality">
      <h2 className="font-medium text-gray-900 mb-1 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-600" /> Data Quality &amp; Validation
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Control which machine data flows into Reports. Use these tools to
        keep test/demo punches out of production statistics.
      </p>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5" data-testid="eto-dq-summary">
          <Stat label="Attendance rows" value={summary.total_attendance_rows} />
          <Stat label="Unmapped rows" value={summary.unmapped_attendance_rows} highlight={summary.unmapped_attendance_rows > 0} />
          <Stat label="Inactive mappings" value={summary.inactive_mappings} />
          <Stat label="Pending mappings" value={summary.pending_mappings} highlight={summary.pending_mappings > 0} />
          <Stat label="Discarded syncs" value={summary.discarded_syncs} />
        </div>
      )}

      {/* Cutoff date */}
      <div className="border border-gray-200 rounded-md p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-4 h-4 text-blue-600" />
          <h3 className="font-medium text-sm text-gray-900">Reports data cutoff</h3>
        </div>
        <p className="text-xs text-gray-600 mb-3">
          Machine rows with a date <em>before</em> this cutoff will be ignored
          everywhere — Reports computations, conflict detection, and absence
          flagging. Set this to your go-live date to wipe all pre-go-live
          test data from view in one click.
        </p>
        <div className="flex items-end gap-2 flex-wrap">
          <Field label="Cutoff date (data before this is ignored)">
            <input
              type="date"
              value={cutoff || ''}
              onChange={(e) => setCutoff(e.target.value)}
              data-testid="eto-cutoff-input"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </Field>
          <button
            onClick={saveCutoff}
            disabled={saving}
            data-testid="eto-cutoff-save"
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save cutoff'}
          </button>
          {cfg?.reports_data_cutoff_date && (
            <button
              onClick={() => { setCutoff(''); setTimeout(saveCutoff, 50); }}
              className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50"
              data-testid="eto-cutoff-clear"
            >
              Clear
            </button>
          )}
        </div>
        {cfg?.reports_data_cutoff_date && (
          <p className="text-xs text-amber-700 mt-2 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Currently active · ignoring machine data before <strong className="ml-1">{cfg.reports_data_cutoff_date}</strong>
          </p>
        )}
      </div>

      {/* Date-range purge */}
      <div className="border border-red-200 rounded-md p-4 bg-red-50/30">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 className="w-4 h-4 text-red-600" />
          <h3 className="font-medium text-sm text-gray-900">Purge attendance by date range</h3>
        </div>
        <p className="text-xs text-gray-700 mb-3">
          Hard-deletes every machine row and every pending action whose date
          falls in [from, to]. <strong>Irreversible.</strong> Use this to
          remove a specific window of bad data (e.g. demo punches you tested
          with). The cutoff date is the safer alternative if you just want
          to hide pre-go-live data.
        </p>
        <div className="flex items-end gap-2 flex-wrap">
          <Field label="From">
            <input
              type="date"
              value={purgeFrom}
              onChange={(e) => setPurgeFrom(e.target.value)}
              data-testid="eto-purge-from"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </Field>
          <Field label="To">
            <input
              type="date"
              value={purgeTo}
              onChange={(e) => setPurgeTo(e.target.value)}
              data-testid="eto-purge-to"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </Field>
          <Field label="Type PURGE to confirm">
            <input
              type="text"
              value={purgeConfirm}
              onChange={(e) => setPurgeConfirm(e.target.value)}
              data-testid="eto-purge-confirm"
              placeholder="PURGE"
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
            />
          </Field>
          <button
            onClick={runPurge}
            disabled={purging || purgeConfirm !== 'PURGE' || !purgeFrom || !purgeTo}
            data-testid="eto-purge-run"
            className="px-4 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-3.5 h-3.5" />
            {purging ? 'Purging…' : 'Purge'}
          </button>
        </div>
      </div>
    </section>
  );
};

// ─── Mappings management (active_for_reports toggle) ────────────────────────

const MappingsManagementSection = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('adminToken')}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/etimeoffice/mappings`, { headers: auth() });
      setMappings(r.data.items || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (empcode, currentValue) => {
    try {
      await axios.patch(`${API}/api/admin/etimeoffice/mappings/${encodeURIComponent(empcode)}`,
        { active_for_reports: !currentValue }, { headers: auth() });
      toast.success(!currentValue
        ? `${empcode} included in Reports`
        : `${empcode} excluded from Reports`);
      load();
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-6" data-testid="eto-mappings-section">
      <h2 className="font-medium text-gray-900 mb-1 flex items-center gap-2">
        <Users2 className="w-4 h-4 text-purple-600" /> Employee mappings
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Approved E-TIMEOFFICE empcodes mapped to Girgit employees. Toggle
        any row OFF to exclude that empcode from Reports and from future
        conflict/absence/comp-off detection. Useful for demo employees or
        terminated staff still on the machine.
      </p>
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : mappings.length === 0 ? (
        <p className="text-sm text-gray-500">No approved mappings yet. Run a sync and approve mappings in the queue.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Empcode</th>
              <th className="text-left px-3 py-2">Name on machine</th>
              <th className="text-left px-3 py-2">Mapped to (Girgit)</th>
              <th className="text-center px-3 py-2">Active in Reports</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((m) => {
              const active = m.active_for_reports !== false;
              return (
                <tr key={m.empcode} className="border-t border-gray-100" data-testid={`mapping-row-${m.empcode}`}>
                  <td className="px-3 py-2 font-mono text-xs">{m.empcode}</td>
                  <td className="px-3 py-2 text-gray-700">{m.name_seen || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">
                    <span className="text-xs text-gray-500">{m.employee_id ? m.employee_id.slice(-8) : '—'}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => toggle(m.empcode, active)}
                      data-testid={`mapping-toggle-${m.empcode}`}
                      className={`px-3 py-1 rounded-md text-xs font-medium ${
                        active
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {active ? '✓ Active' : '✗ Excluded'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default EtimeofficeSettingsPage;
